import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/cn";
import { getProvider, type ProviderConfig } from "@/api/providers";
import {
  ProviderHeroCard,
  ProviderMetadataSections,
  ProviderHowItWorks,
  canSelectForScan,
} from "@/components/providers/provider-detail-sections";
import {
  useDiscoveryProfile,
  useSaveDiscoveryProfile,
} from "@/hooks/useDiscovery";

function PortalsProviderSidebar({ provider }: { provider: ProviderConfig }) {
  const { data: profile } = useDiscoveryProfile();
  const saveMutation = useSaveDiscoveryProfile();
  const selectedBoards = new Set(profile?.jobBoards ?? []);
  const isSelected = selectedBoards.has(provider.providerId);
  const selectable = canSelectForScan(provider);

  async function toggleScan() {
    const current = profile?.jobBoards ?? [];
    const next = isSelected
      ? current.filter((id) => id !== provider.providerId)
      : [...current, provider.providerId];
    await saveMutation.mutateAsync({ jobBoards: next });
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Your scan
        </h3>
        {selectable ? (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {isSelected
                ? "This board aggregator is included in your Free Scan on Explore."
                : "Add this board aggregator to include it in your Free Scan on Explore."}
            </p>
            <button
              type="button"
              onClick={() => toggleScan()}
              disabled={saveMutation.isPending}
              className={cn(
                "w-full rounded-md px-3 py-2 text-sm font-medium transition",
                isSelected
                  ? "border border-border text-foreground hover:bg-muted/20"
                  : "bg-brand text-brand-foreground hover:opacity-90",
              )}
            >
              {saveMutation.isPending
                ? "Saving…"
                : isSelected
                  ? "Remove from my scan"
                  : "Add to my scan"}
            </button>
          </div>
        ) : provider.type === "board" && !provider.enabled ? (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              This provider is disabled in the global catalog. Enable it before adding to your
              scan.
            </p>
            <Link
              to={`/settings/providers/${provider.providerId}`}
              className="block rounded-md bg-brand px-3 py-2 text-center text-sm font-medium text-brand-foreground transition hover:opacity-90"
            >
              Open in catalog
            </Link>
          </div>
        ) : provider.type === "ats" ? (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              ATS providers are activated by tracking companies that use them.
            </p>
            <Link
              to="/explore?sources=1"
              className="block rounded-md bg-brand px-3 py-2 text-center text-sm font-medium text-brand-foreground transition hover:opacity-90"
            >
              Configure My sources
            </Link>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Custom parsers are configured through CLI settings, not board selection.
          </p>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Links
        </h3>
        <div className="flex flex-col gap-2">
          <Link
            to={`/settings/providers/${provider.providerId}`}
            className="rounded-md border border-border px-3 py-2 text-center text-sm text-foreground transition hover:bg-muted/20"
          >
            Global catalog settings
          </Link>
          <Link
            to="/explore"
            className="rounded-md border border-border px-3 py-2 text-center text-sm text-foreground transition hover:bg-muted/20"
          >
            Open Find Jobs
          </Link>
          {provider.website && (
            <a
              href={provider.website}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-border px-3 py-2 text-center text-sm text-foreground transition hover:bg-muted/20"
            >
              Visit website
            </a>
          )}
          {provider.docsUrl && (
            <a
              href={provider.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-border px-3 py-2 text-center text-sm text-foreground transition hover:bg-muted/20"
            >
              View documentation
            </a>
          )}
        </div>
      </div>

      <ProviderHowItWorks type={provider.type} />
    </div>
  );
}

export function PortalsProviderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: profile } = useDiscoveryProfile();
  const selectedBoards = new Set(profile?.jobBoards ?? []);

  const { data: provider, isLoading, error } = useQuery<ProviderConfig>({
    queryKey: ["provider", id],
    queryFn: () => getProvider(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const isSelected = provider ? selectedBoards.has(provider.providerId) : false;
  const selectable = provider ? canSelectForScan(provider) : false;

  return (
    <div className="space-y-6">
      <header className="mb-8">
        <Link
          to="/settings/discovery?tab=providers"
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          &larr; Back to discovery sources
        </Link>
      </header>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}
      {error && <p className="text-sm text-destructive">Failed to load provider.</p>}

      {provider && (
        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          <div className="mb-6 space-y-4 lg:col-span-1 lg:mb-0">
            <PortalsProviderSidebar provider={provider} />
          </div>

          <div className="space-y-4 lg:col-span-2">
            <ProviderHeroCard
              provider={provider}
              badges={
                <>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[10px]",
                      provider.enabled
                        ? "bg-success/10 text-success"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {provider.enabled ? "Globally enabled" : "Globally disabled"}
                  </span>
                  {selectable && (
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[10px]",
                        isSelected
                          ? "bg-brand/10 text-brand"
                          : "bg-muted text-muted-foreground",
                      )}
                    >
                      {isSelected ? "In your scan" : "Not in your scan"}
                    </span>
                  )}
                </>
              }
            />
            <ProviderMetadataSections provider={provider} />
          </div>
        </div>
      )}
    </div>
  );
}
